
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useBlogPosts from '../hooks/useBlogPosts';

const Blog = () => {
  const { posts: blogPosts, loading } = useBlogPosts();
  // Structured data for blog listing page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Vaulto Blog - Instagram Backup & Security",
    "description": "Expert guides on Instagram backup, account security, and protecting your digital content. Learn how to save Instagram posts, stories, and reels to Google Drive.",
    "url": "https://vaulto.social/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Vaulto",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vaulto.social/vaulto.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription || post.excerpt,
      "url": `https://vaulto.social/blog/${post.slug}`,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Vaulto Blog - Instagram Backup Guides & Security Tips</title>
        <meta name="title" content="Vaulto Blog - Instagram Backup Guides & Security Tips" />
        <meta name="description" content="Expert guides on Instagram backup, account security, content protection, and social media safety. Learn how to automatically save Instagram posts, stories, and reels to Google Drive." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vaulto.social/blog" />
        
        {/* Keywords */}
        <meta name="keywords" content="instagram backup guide, instagram security tips, save instagram content, protect instagram account, automatic instagram backup, instagram to google drive, social media backup" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vaulto.social/blog" />
        <meta property="og:title" content="Vaulto Blog - Instagram Backup Guides & Security Tips" />
        <meta property="og:description" content="Expert guides on Instagram backup, account security, and protecting your digital content. Learn how to save your Instagram posts, stories, and reels." />
        <meta property="og:image" content="https://vaulto.social/vaulto.png" />
        <meta property="og:site_name" content="Vaulto" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://vaulto.social/blog" />
        <meta name="twitter:title" content="Vaulto Blog - Instagram Backup Guides & Security Tips" />
        <meta name="twitter:description" content="Expert guides on Instagram backup, account security, and protecting your digital content." />
        <meta name="twitter:image" content="https://vaulto.social/vaulto.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <Navbar />
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-violet-600 mb-4">
            Vaulto Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Insights, tips, and guides on protecting your digital legacy and mastering social media security.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading && (
            <div className="col-span-full text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
              <p className="mt-4 text-gray-500">Loading posts...</p>
            </div>
          )}
          {!loading && blogPosts.map((post) => (
            <article key={post.id} className="flex flex-col bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-pink-500/20 transition-all duration-300 border border-gray-200 hover:border-pink-400">
              <Link to={`/blog/${post.slug}`} className="block group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-10 group-hover:bg-opacity-0 transition-all duration-300"></div>
                </div>
              </Link>
              
              <div className="flex-1 p-6 flex flex-col">
                <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                  <span>{new Date(post.date).toLocaleDateString()}</span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span>{post.readTime}</span>
                </div>
                
                <Link to={`/blog/${post.slug}`} className="block mt-2 mb-4">
                  <h2 className="text-xl font-bold text-gray-900 hover:text-pink-600 transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                </Link>
                
                <p className="text-gray-600 mb-6 line-clamp-3 flex-grow">
                  {post.excerpt}
                </p>
                
                <div className="mt-auto pt-4 border-t border-gray-200 flex justify-between items-center">
                  <div className="text-sm text-gray-700 font-medium">
                    By {post.author}
                  </div>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="text-pink-600 hover:text-pink-700 font-medium text-sm flex items-center group"
                  >
                    Read Article
                    <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Blog;

